import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, forkJoin, of, from } from 'rxjs';
import { takeUntil, take, switchMap, catchError, tap } from 'rxjs/operators';

import { UserService } from '@core/services/user.service';
import { CompanyService } from '@core/services/company.service';
import { AppPlanService } from '@core/services/app-plan.service';
import { ThemeService } from '@core/services/theme.service';
import {
  TierAccessService,
  UserTier,
} from '@core/services/tier-access.service';
import {
  PaymentService,
  UpgradeRequest,
} from '@core/services/payment.service';

import { User } from '@core/models/user';
import { Company } from '@core/models/company';
import { AppPlan } from '@core/models/app-plan';
import { ADVERTISE_SUBSCRIPTION_PRICING } from '@core/config/subscription-pricing';
import { LoggingService } from '@core/services/logging.service';
import { AppAddOn, BillingInterval } from '@core/models/app-addon';
import {
  PROMO_CODES_ENABLED,
  PromoCode,
  PromotionCodeRefusal,
  applyPromoToCents,
  promoAppliesToPlan,
  promoFromCheck,
} from '@core/config/promotions';
import { PromotionCodeService } from '@core/services/promotion-code.service';
import {
  ADDON_REQUEST_FAILED_MESSAGE,
  AddonChangeResult,
  AddonRequestService,
} from '@core/services/addon-request.service';
import { PARTNER_PLAN_MESSAGE, isChannelPartnerCompany } from '@core/utils/channel-partner';
import { firstValueFrom } from 'rxjs';
import { ROADMAP_ITEMS } from '@core/data/roadmap.data';
import {
  SCAN_USAGE_TIERS,
  TOKEN_USAGE_INCLUDED,
  TOKEN_USAGE_METERED,
} from '@core/data/token-usage.data';
import { environment } from '@environments/environment';
import {
  CheckoutBarSummary,
  canPurchaseForCompany,
  clearCheckoutSelection,
  promoThenText,
  readCheckoutSelection,
  saveCheckoutSelection,
} from '@core/utils/checkout-selection';
import { PlanStep } from '@core/partials/plan-steps/plan-steps.component';

/**
 * SelectPlanPartialComponent
 *
 * Displays available subscription plans and handles plan selection.
 * Can operate in company-based mode (multi-tenant) or user-based mode.
 */
@Component({
  selector: 'app-select-plan-partial',
  templateUrl: './select-plan-partial.component.html',
  styleUrls: ['./select-plan-partial.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectPlanPartialComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly companyService = inject(CompanyService);
  private readonly planService = inject(AppPlanService);
  private readonly paymentService = inject(PaymentService);
  public readonly tierAccessService = inject(TierAccessService);
  private readonly loggingService = inject(LoggingService);
  private readonly promotionCodeService = inject(PromotionCodeService);
  private readonly addonRequestService = inject(AddonRequestService);

  /** The note a channel-partner company sees instead of prices (#943). */
  public readonly partnerPlanMessage = PARTNER_PLAN_MESSAGE;

  /** True for a channel-partner company: no prices, no checkout (#943). */
  public get isPartnerCompany(): boolean {
    return isChannelPartnerCompany(this.company);
  }

  /** Annual is default to prevent interval mismatch. */
  /**
   * Whether plan/add-on price amounts are advertised. When false the cards show
   * "Contact us" and paid plans route to sales instead of Stripe checkout.
   * @see ADVERTISE_SUBSCRIPTION_PRICING
   */
  public readonly advertisePricing = ADVERTISE_SUBSCRIPTION_PRICING;

  /** Default trial length, for cards on plans that do not set their own. */

  /**
   * Whether the coupon input is offered. Off until the checkout API applies the
   * coupon, so we never quote a discount Stripe will not honour.
   * @see PROMO_CODES_ENABLED
   */
  public readonly promoCodesEnabled = PROMO_CODES_ENABLED;

  /** What we are building next — replaces the add-on module grid. */
  public readonly roadmapItems = ROADMAP_ITEMS;

  /** Coin usage reference shown under the plans. */
  public readonly tokenUsageMetered = TOKEN_USAGE_METERED;
  public readonly tokenUsageIncluded = TOKEN_USAGE_INCLUDED;
  /** On-demand scans are priced by type and asset count, not a flat rate. */
  public readonly scanUsageTiers = SCAN_USAGE_TIERS;
  public showTokenUsage = false;

  /** Raw text in the coupon box. */
  public promoCodeInput = '';
  /** The applied promotion, or null when none is active. */
  public appliedPromo: PromoCode | null = null;
  /** Set when an entered code was rejected, cleared on the next attempt. */
  public promoError: string | null = null;
  /** Why the code was refused (#956): not valid, or "try again later". */
  public promoErrorKind: PromotionCodeRefusal | null = null;
  /** True while the API checks a code. */
  public promoChecking = false;

  /** What the sticky checkout bar shows (#956), or null to hide it. */
  public checkoutSummary: CheckoutBarSummary | null = null;
  /** True while the bar's action runs. */
  public checkoutBusy = false;
  /** Read out by the live region when a plan is chosen or cleared. */
  public selectionAnnouncement = '';
  private selectionRestored = false;

  public billingInterval: Exclude<BillingInterval, 'free' | 'one_time'> = 'annual';

  /** Add-ons loaded from the shared JSON source. */
  public addOns: AppAddOn[] = [];
  /** Billable add-ons selected for the company. */
  public selectedAddOns: string[] = [];
  /** Coming-soon selections (stored as intent only; not billed). */
  public selectedAddOnIntents: string[] = [];
  public selectedPlanId: string | null = null;

  @Input() returnRoute = '/dashboard';
  @Input() showCompanyBased = true;
  @Input() isRegistrationFlow = false;

  // State - initialized to stable values to avoid undefined->false flips
  private user: User | null = null;
  public company: Company | null = null;
  public plans: AppPlan[] = [];
  public isLoading = true;

  // Cached features to avoid recomputation in template
  private featuresCache = new Map<number, any[]>();

  ngOnInit(): void {
    this.themeService.setSelectPlanPageLayout(true);
    this.themeService.showSpinner();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Navigate to dashboard - used by the "Continue to dashboard" button
   * Uses programmatic navigation to ensure proper routing
   */
  navigateToDashboard(): void {
    // Ensure user data is synced to localStorage before navigation
    const currentUser = this.userService.user;
    if (currentUser && typeof localStorage !== 'undefined') {
      // Ensure intendedTier is set for free-tier check in guards
      if (this.company?.tier === 'free' && !currentUser.intendedTier) {
        currentUser.intendedTier = 'free';
      }
      localStorage.setItem('user', JSON.stringify(currentUser));
      // Also update userModelSubject to ensure guards get fresh data
      this.userService.setUserModel(currentUser);
    }

    // Set company in companyService for guards to access
    if (this.company) {
      this.companyService.company = this.company;
    }

    // Clear any stale accountExpired flag that might block access
    localStorage.removeItem('accountExpired');

    // Navigate to dashboard
    this.router.navigate(['/dashboard']).then(success => {
      if (!success) {
        console.error('❌ Navigation to dashboard failed, trying direct URL');
        window.location.href = '/dashboard';
      }
    }).catch(err => {
      console.error('❌ Navigation error:', err);
      window.location.href = '/dashboard';
    });
  }

  /**
   * Load all required data in parallel, then finalize loading state.
   * Uses RxJS to coordinate async operations cleanly.
   */
  private loadData(): void {
    // Get user first (take(1) to get current value), then load company if needed
    this.userService
      .getUserModel()
      .pipe(
        take(1),
        switchMap((userModel: User | null) => {
          this.user = userModel;

          // Load plans regardless of user
          const plans$ = this.planService.getOrderedPlans().pipe(
            take(1),
            tap((plans: AppPlan[]) => {
              this.plans = plans;
              this.featuresCache.clear();
            }),
            catchError((err) => {
              console.error('Error loading plans:', err);
              return of([]);
            }),
          );

          const addOns$ = this.planService.getAddOnsFromJson().pipe(
            take(1),
            tap((addOns: AppAddOn[]) => {
              this.addOns = addOns.filter((a) => a.type !== 'credit_pack');
            }),
            catchError((err) => {
              console.error('Error loading add-ons:', err);
              return of([]);
            }),
          );

          // Load company if in company-based mode and user has a company
          const currentCompanyId = userModel?.currentCompanyId;
          const company$ =
            this.showCompanyBased && currentCompanyId
              ? from(this.companyService.getCompany(currentCompanyId)).pipe(
                  tap((result: any) => {
                    if (result) {
                      this.company =
                        result instanceof Company
                          ? result
                          : new Company(result);
                    }
                  }),
                  catchError((err) => {
                    console.error('Error loading company:', err);
                    return of(null);
                  }),
                )
              : of(null);

          return forkJoin([plans$, addOns$, company$]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.finalizeLoading();
        },
        error: (err) => {
          console.error('Error loading data:', err);
          this.finalizeLoading();
        },
      });
  }

  /**
   * Finalize loading state safely outside of change detection.
   */
  private finalizeLoading(): void {
    // Use setTimeout to ensure we're outside Angular's change detection cycle
    setTimeout(() => {
      this.isLoading = false;
      this.themeService.hideSpinner();

      // Auto-select current plan if one exists (allows addon modifications)
      if (this.company && this.company.planId) {
        const normalizedPlanId = this.planService.normalizePlanId(this.company.planId);
        const currentPlan = this.plans.find((p) => p.uid === normalizedPlanId);
        if (currentPlan) {
          this.selectedPlanId = currentPlan.uid;
          // Load existing addons
          if (this.company.addons && Array.isArray(this.company.addons)) {
            this.selectedAddOns = [...this.company.addons];
          }
        }
      }

      this.restoreSelection();
      this.refreshCheckout();
      this.cdr.markForCheck();
    }, 0);
  }

  /**
   * Convert features object to array, filtering out disabled boolean features.
   * Does NOT mutate the input - creates a new array.
   */
  getFeaturesArray(features: { [key: string]: any }): any[] {
    if (!features) return [];

    // New schema supports string arrays.
    if (Array.isArray(features)) {
      return (features as any[])
        .filter((f) => typeof f === 'string' && f.trim().length > 0)
        .map((name) => ({ name, type: 'boolean', value: true }));
    }

    return Object.values(features).filter(
      (f) => !(f.type === 'boolean' && !f.value),
    );
  }

  /**
   * Handle plan selection (highlights the plan, shows add-ons)
   * Does not proceed until confirmPlanSelection is called
   */
  public selectPlan(planId: string): void {
    if (this.showCompanyBased && this.company && !this.canPurchase) return;
    this.selectedPlanId = planId;
    const name = this.plans.find((p) => p.uid === planId)?.name;
    this.selectionAnnouncement = name
      ? `${name} plan selected. Finish checkout below to activate it.`
      : '';

    // If the chosen plan doesn't support the current interval, force annual.
    if (!this.isBillingIntervalAllowedForSelectedPlan(this.billingInterval)) {
      this.billingInterval = 'annual';
    }

    // Drop add-ons that are no longer valid for the selected plan.
    this.normalizeSelectedAddOnsForPlan();
    this.refreshCheckout();
    this.persistSelection();

    // Scroll to add-ons section
    setTimeout(() => {
      const addonsSection = document.querySelector('.cyber-addons-section');
      if (addonsSection) {
        addonsSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  }

  /**
   * Confirm plan selection and proceed with registration/upgrade
   */
  async confirmPlanSelection(): Promise<void> {
    if (!this.selectedPlanId) {
      this.themeService.triggerError('Please select a plan first');
      return;
    }

    try {
      this.themeService.showSpinner();

      if (this.showCompanyBased && this.company) {
        // Company-based plan selection - check if it's a premium upgrade
        const plan = this.plans.find((p) => p.uid === this.selectedPlanId);
        if (!plan) {
          throw new Error('Plan not found');
        }

        // Check if plan selection changed or if just updating addons
        const normalizedCurrentPlanId = this.company.planId
          ? this.planService.normalizePlanId(this.company.planId)
          : null;
        const isPlanChanging = normalizedCurrentPlanId !== this.selectedPlanId;

        // If plan isn't changing, just update addons for existing plan
        if (!isPlanChanging && normalizedCurrentPlanId === this.selectedPlanId) {
          const result = await this.requestCompanyAddOnChanges();
          this.themeService.hideSpinner();
          if (!result.success) {
            this.themeService.triggerError(result.message);
            return;
          }
          this.themeService.triggerSuccess(result.message);
          this.router.navigate([this.returnRoute]);
          return;
        }

        // Contact-sales plans — and every paid plan while prices are
        // unadvertised — do not go through self-serve checkout.
        if (plan.flags?.contactSales || (!this.advertisePricing && !plan.isFree)) {
          this.themeService.hideSpinner();
          this.goToSupportRequest();
          return;
        }

        // For premium plans, use the payment service for checkout
        if (plan.appTier === UserTier.PREMIUM) {
          // Create upgrade request matching the service interface
          // Look up the real Stripe price ID from environment config
          const planKey = plan.uid.replace(/_/g, '').toLowerCase(); // e.g. "security_essentials" -> "securityessentials"
          const billingKey = this.billingInterval === 'monthly' ? 'monthly' : 'annual';
          const priceIdKey = `${planKey}${billingKey}` as keyof typeof environment.stripe.priceIds;
          const priceId = environment.stripe.priceIds[priceIdKey];

          if (!priceId) {
            throw new Error(`No Stripe price configured for plan: ${plan.uid} (${billingKey})`);
          }

          const upgradeRequest: UpgradeRequest = {
            currentCompanyId: this.company.uid,
            targetTier: UserTier.PREMIUM,
            planId: plan.uid,
            priceId,
            billingInterval: billingKey,
            source: 'manual',
          };

          // Process upgrade through payment service
          const result =
            await this.paymentService.processUpgrade(upgradeRequest);

          if (result.success) {
            // Payment service will handle the redirect to Stripe
            // Don't navigate away yet, let Stripe handle the flow
            return;
          } else {
            throw new Error(result.error || 'Upgrade failed');
          }
        } else {
          // No plan is free or self-serve trial any more (#847); anything that
          // is not a paid checkout goes to sales.
          this.themeService.hideSpinner();
          this.goToSupportRequest();
          return;
        }
      } else if (this.user) {
        // Legacy user-based plan selection
        const plan = this.plans.find((p) => p.uid === this.selectedPlanId);
        if (plan) {
          this.user.planId = plan.uid;
          await this.userService.update(this.user, this.user.uid);
        }
      }

      this.themeService.hideSpinner();
      this.router.navigate([this.returnRoute]);
    } catch (error) {
      console.error('Error selecting plan:', error);
      this.themeService.hideSpinner();
      this.themeService.triggerError(
        'Failed to select plan. Please try again.',
      );
    }
  }

  private async updateCompanyPlan(planId: string): Promise<void> {
    if (!this.company) return;

    // Map plan names to tier types
    const plan = this.plans.find((p) => p.uid === planId);
    if (!plan) return;

    // Contact-sales plans, and every paid plan while prices are unadvertised,
    // go to sales rather than checkout: we cannot charge against a price the
    // customer was never shown.
    if (plan.flags?.contactSales || (!this.advertisePricing && !plan.isFree)) {
      this.goToSupportRequest();
      return;
    }

    // Check if this is a premium plan that requires payment
    if (plan.appTier === UserTier.PREMIUM) {
      // Redirect to payment processor for premium plans
      await this.handlePremiumUpgrade(plan);
    } else {
      // No plan is free or self-serve trial any more (#847).
      this.goToSupportRequest();
    }
  }

  private async handlePremiumUpgrade(plan: AppPlan): Promise<void> {
    if (!this.company) return;

    try {
      // Get price ID for the plan (would come from configuration or API)
      // Undefined when not configured: the API then resolves the price by its
      // Stripe lookup key (#870).
      const priceId = this.getPriceIdForPlan(plan.uid, this.billingInterval);

      // Create checkout session and redirect to Stripe
      const result = await this.paymentService.createCheckoutSession(
        this.company,
        plan.uid,
        priceId,
        this.billingInterval,
        promoAppliesToPlan(this.appliedPromo, plan.uid)
          ? this.appliedPromo!.code
          : undefined,
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to initiate payment');
      }

      // The payment service will handle the redirect to Stripe Checkout
      // User will return to the success URL after completing payment
    } catch (error: any) {
      console.error('Premium upgrade error:', error);
      this.themeService.triggerError(
        `Failed to start premium upgrade: ${error.message}`,
      );

      // Offer alternative - create support ticket
      if (
        confirm(
          'Would you like to create a support ticket for manual upgrade assistance?',
        )
      ) {
        await this.createUpgradeTicket(plan);
      }
    }
  }

  private async createUpgradeTicket(plan: AppPlan): Promise<void> {
    if (!this.company) return;

    try {
      // Get current user - simplified approach
      const user = this.user; // Use the existing user property

      if (!user) {
        this.themeService.triggerError(
          'Unable to create support ticket - user not found',
        );
        return;
      }

      const ticketData = {
        currentCompanyId: this.company.uid,
        userEmail: user.email,
        planRequested: plan.name,
        message: `Request for ${plan.name} upgrade assistance. User attempted online upgrade but encountered technical difficulties.`,
        priority: 'high' as const,
      };

      const success = await this.paymentService.createUpgradeTicket(ticketData);

      if (success) {
        this.themeService.triggerSuccess(
          'Support ticket created! Our team will contact you within 24 hours to assist with your upgrade.',
        );
      }
    } catch (error) {
      console.error('Failed to create support ticket:', error);
      this.themeService.triggerError(
        'Failed to create support ticket. Please contact support directly.',
      );
    }
  }

  /**
   * Maps a plan and billing interval to its corresponding Stripe price ID.
   *
   * First checks environment configuration for explicit price ID mapping,
   * then falls back to conventional format: price_{plan_uid}_{interval}
   *
   * @param planId - The plan UID to get pricing for
   * @param interval - Billing interval ('monthly' or 'annual')
   * @returns Stripe price ID, or undefined so the API resolves it by lookup key
   *
   * @remarks
   * Price IDs must be created in Stripe Dashboard and configured in environment files.
   * Format examples:
   * - environment.stripe.priceIds.securityessentialsmonthly = 'price_xxx'
   * - Fallback: 'price_security_essentials_monthly'
   */
  private getPriceIdForPlan(
    planId: string,
    interval: Exclude<BillingInterval, 'free' | 'one_time'>,
  ): string | undefined {
    // Normalize plan UID: remove hyphens AND underscores, convert to lowercase to match environment keys
    // e.g., 'security_essentials' or 'security-essentials' -> 'securityessentials'
    const planKey = planId.replace(/[-_]/g, '').toLowerCase();
    const intervalKey = interval === 'monthly' ? 'monthly' : 'annual';
    const envKey = `${planKey}${intervalKey}`;

    // Try to get from environment.stripe.priceIds
    const priceIds = environment.stripe?.priceIds as Record<string, string> | undefined;
    if (priceIds && priceIds[envKey]) {
      return priceIds[envKey];
    }

    // Not configured: the API resolves the price by lookup key.
    return undefined;
  }

  /**
   * Toggle between annual and monthly billing intervals.
   *
   * @param interval - The billing interval to set ('annual' or 'monthly')
   *
   * @remarks
   * This method updates the billing interval and normalizes selected add-ons
   * to ensure they are valid for the selected plan under the new interval.
   */
  public setBillingInterval(interval: Exclude<BillingInterval, 'free' | 'one_time'>): void {
    this.billingInterval = interval;
    // Drop add-ons that aren't valid for the selected plan after interval change.
    this.normalizeSelectedAddOnsForPlan();
    this.refreshCheckout();
    this.persistSelection();
  }

  /**
   * Determine if a billing interval is allowed for the selected plan.
   *
   * @param interval - The billing interval to check ('annual' or 'monthly')
   * @returns `true` if the interval is allowed for the selected plan, `false` otherwise
   *
   * @remarks
   * - Returns `true` for 'annual' if plan has no billing configuration or includes 'free'
   * - Otherwise checks if the plan's billing array includes the specified interval
   */
  public isBillingIntervalAllowedForSelectedPlan(
    interval: Exclude<BillingInterval, 'free' | 'one_time'>,
  ): boolean {
    const plan = this.getSelectedPlan();
    if (!plan?.billing) return interval === 'annual';
    if (plan.billing.includes('free')) return interval === 'annual';
    return plan.billing.includes(interval);
  }

  /**
   * Convert cents to dollars for use with Angular currency pipe.
   *
   * @param cents - The amount in cents to convert (nullable)
   * @returns The amount in dollars as a number (0 if input is invalid)
   */
  public centsToDollars(cents: number | null | undefined): number {
    const safe = typeof cents === 'number' && isFinite(cents) ? cents : 0;
    return safe / 100;
  }

  /** Selected plan numeric tier (0=Free, 1=Startup, ...). */
  public getSelectedPlanTier(): number {
    const plan = this.getSelectedPlan();
    return typeof plan?.tier === 'number' ? plan.tier : 0;
  }

  /** Plan list price in cents for the currently selected billing interval. */
  public getPlanListPriceCents(plan: AppPlan | undefined): number {
    if (!plan) return 0;
    return this.billingInterval === 'monthly'
      ? plan.priceMonthlyCents ?? 0
      : plan.priceAnnualCents ?? 0;
  }

  /**
   * Plan price in cents for the selected interval, with any applied promotion.
   * Falls back to list price whenever the promotion does not cover the plan.
   */
  public getPlanPriceCents(plan: AppPlan | undefined): number {
    return applyPromoToCents(
      this.getPlanListPriceCents(plan),
      this.appliedPromo,
      plan?.uid,
    );
  }

  /** True when this plan is showing a discounted price, so the card can strike the list price. */
  public isPlanDiscounted(plan: AppPlan | undefined): boolean {
    return promoAppliesToPlan(this.appliedPromo, plan?.uid);
  }

  /** Checks whatever is in the coupon box with the API (#943): a preview only. */
  public async applyPromoCode(): Promise<void> {
    const entered = this.promoCodeInput.trim();
    if (!entered) {
      this.appliedPromo = null;
      this.promoError = null;
      this.promoErrorKind = null;
      this.refreshCheckout();
      return;
    }

    const interval = this.billingInterval === 'monthly' ? 'monthly' : 'annual';
    this.promoChecking = true;
    try {
      const result = await firstValueFrom(this.promotionCodeService.check(entered, interval));
      if (!result.valid) {
        this.appliedPromo = null;
        this.promoError = result.message;
        this.promoErrorKind = result.reason ?? 'invalid';
      } else {
        this.appliedPromo = promoFromCheck(result);
        this.promoError = null;
        this.promoErrorKind = null;
        this.loggingService.logInfo('Promotion code applied', { code: result.code });
      }
    } finally {
      this.promoChecking = false;
      this.refreshCheckout();
      this.persistSelection();
      this.cdr.markForCheck();
    }
  }

  /** Drop the applied promotion and return every card to list price. */
  public clearPromoCode(): void {
    this.appliedPromo = null;
    this.promoError = null;
    this.promoErrorKind = null;
    this.promoCodeInput = '';
    this.refreshCheckout();
    this.persistSelection();
  }

  public toggleTokenUsage(): void {
    this.showTokenUsage = !this.showTokenUsage;
  }

  /**
   * Only Coin packs are self-serve here. Compliance and module add-ons are
   * sold through sales, so they are not rendered as a purchasable grid.
   */
  public get tokenPacks(): AppAddOn[] {
    return this.addOns.filter((addOn) => addOn.type === 'credit_pack');
  }

  /** Add-on price in cents (billable only). */
  public getAddOnBillablePriceCents(addOn: AppAddOn): number {
    if (addOn.flags?.comingSoon && addOn.flags?.billableNow === false) {
      return 0;
    }
    return this.billingInterval === 'monthly'
      ? addOn.priceMonthlyCents
      : addOn.priceAnnualCents;
  }

  public getBillingLabel(): string {
    return this.billingInterval === 'monthly' ? '/month' : '/year';
  }

  /** Monthly equivalent for annual billing (for display only). */
  public getAnnualEquivalentMonthlyDollars(plan: AppPlan | undefined): number {
    const annual = applyPromoToCents(
      plan?.priceAnnualCents ?? 0,
      this.appliedPromo,
      plan?.uid,
    );
    return this.centsToDollars(Math.round(annual / 12));
  }

  /**
   * Route the user to the in-app support request form for a sales conversation.
   *
   * Sales-assisted purchases stay inside the dashboard: the user is already
   * signed in, so the in-app ticket carries their identity and company context.
   * Navigate rather than `window.open` — a new tab loses the SPA state and
   * leaves the customer looking at a second copy of the app.
   */
  private goToSupportRequest(): void {
    this.router.navigate(['/dashboard/support/create']).catch(() => {
      this.loggingService.logError(
        'Failed to navigate to the support request form for a sales inquiry.',
      );
    });
  }

  /**
   * Validate if an add-on can be selected for the currently selected plan.
   *
   * @param addOn - The add-on to validate
   * @returns `true` if the add-on can be selected, `false` otherwise
   *
   * @remarks
   * **Important**: When backend integration is implemented, all add-on eligibility rules
   * (minPlanTier, anyOf dependencies, incompatibleWith conflicts) must be re-validated
   * on the server side to prevent users from bypassing client-side restrictions.
   *
   * Client-side validation is for UX only and should not be considered a security boundary.
   */
  private canSelectAddOn(addOn: AppAddOn): boolean {
    const plan = this.getSelectedPlan();
    const planTier = typeof plan?.tier === 'number' ? plan.tier : 0;

    if (addOn.flags?.contactSales) {
      // Not an error — the customer is being routed somewhere useful.
      this.themeService.triggerInfo(
        'Our team scopes this add-on with you. Taking you to the support request form.',
      );
      this.goToSupportRequest();
      return false;
    }

    // Free plans cannot add billable paid add-ons. Use the plan's own flag:
    // the paid Standard plan is tier 0 too, so a tier check would lock it out.
    if (
      (!plan || plan.isFree) &&
      !(addOn.flags?.comingSoon && addOn.flags?.billableNow === false)
    ) {
      this.themeService.triggerError(
        'Add-ons need an active plan. Choose a plan to add modules.',
      );
      return false;
    }

    const minTier = addOn.requires?.minPlanTier;
    if (typeof minTier === 'number' && planTier < minTier) {
      this.themeService.triggerError(
        'This add-on requires a higher plan tier.',
      );
      return false;
    }

    const anyOf = addOn.requires?.anyOf;
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      const hasRequired = anyOf.some((id) => this.selectedAddOns.includes(id));
      if (!hasRequired) {
        const requiredList = anyOf.join(', ');
        this.themeService.triggerError(
          `Select the required add-on(s) first: ${requiredList}.`,
        );
        return false;
      }
    }

    return true;
  }

  /** Remove add-ons/intents that are invalid for the selected plan and enforce dependencies. */
  private normalizeSelectedAddOnsForPlan(): void {
    const plan = this.getSelectedPlan();
    if (!plan) {
      this.selectedAddOns = [];
      this.selectedAddOnIntents = [];
      return;
    }

    const planTier = typeof plan.tier === 'number' ? plan.tier : 0;

    // Filter invalid billable add-ons.
    const resolved: string[] = [];
    for (const id of this.selectedAddOns) {
      const addOn = this.addOns.find((a) => a.id === id);
      if (!addOn) continue;

      if (plan.isFree) continue;
      const minTier = addOn.requires?.minPlanTier;
      if (typeof minTier === 'number' && planTier < minTier) continue;

      // Enforce anyOf requirements.
      // TODO: This single-pass validation has order dependency issues.
      // If add-on B depends on add-on A, but A comes later in selectedAddOns array,
      // B will be incorrectly filtered out. Consider implementing multi-pass resolution
      // or topological sorting to handle complex dependency chains correctly.
      const anyOf = addOn.requires?.anyOf;
      if (Array.isArray(anyOf) && anyOf.length > 0) {
        const hasReq = anyOf.some((reqId) => this.selectedAddOns.includes(reqId));
        if (!hasReq) continue;
      }

      // Enforce incompatibilities - keep the first selected.
      if (
        Array.isArray(addOn.incompatibleWith) &&
        addOn.incompatibleWith.some((conflict) => resolved.includes(conflict))
      ) {
        continue;
      }

      resolved.push(id);
    }
    this.selectedAddOns = resolved;

    // Filter invalid intents (coming-soon selections).
    this.selectedAddOnIntents = this.selectedAddOnIntents.filter((id) => {
      const addOn = this.addOns.find((a) => a.id === id);
      if (!addOn) return false;
      const minTier = addOn.requires?.minPlanTier;
      return !(typeof minTier === 'number' && planTier < minTier);
    });
  }

  // Add-on selection helpers
  public toggleAddon(addonId: string): void {
    const addOn = this.addOns.find((a) => a.id === addonId);
    if (!addOn) return;

    // Coming soon: store intent, do not bill.
    if (addOn.flags?.comingSoon && addOn.flags?.billableNow === false) {
      const idx = this.selectedAddOnIntents.indexOf(addonId);
      if (idx >= 0) {
        this.selectedAddOnIntents.splice(idx, 1);
      } else {
        if (!this.canSelectAddOn(addOn)) return;
        this.selectedAddOnIntents.push(addonId);
      }
      return;
    }

    const idx = this.selectedAddOns.indexOf(addonId);
    if (idx >= 0) {
      this.selectedAddOns.splice(idx, 1);
      return;
    }

    if (!this.canSelectAddOn(addOn)) return;

    // Enforce incompatibilities by removing any conflicting selected add-ons.
    if (Array.isArray(addOn.incompatibleWith) && addOn.incompatibleWith.length) {
      this.selectedAddOns = this.selectedAddOns.filter(
        (id) => !addOn.incompatibleWith!.includes(id),
      );
    }

    this.selectedAddOns.push(addonId);
    this.refreshCheckout();
  }

  public isAddonSelected(addonId: string): boolean {
    return (
      this.selectedAddOns.includes(addonId) ||
      this.selectedAddOnIntents.includes(addonId)
    );
  }

  /**
   * Check if a plan has been selected (enables confirm button)
   */
  public canConfirm(): boolean {
    return this.selectedPlanId !== null;
  }

  /**
   * Get selected plan details for display
   */
  public getSelectedPlan(): AppPlan | undefined {
    return this.selectedPlanId
      ? this.plans.find((p) => p.uid === this.selectedPlanId)
      : undefined;
  }

  /**
   * Calculate total price with add-ons
   */
  public getTotalPrice(): number {
    const plan = this.getSelectedPlan();
    if (!plan) return 0;

    const planCents = this.getPlanPriceCents(plan);
    const addOnsCents = this.selectedAddOns.reduce((sum, addonId) => {
      const addon = this.addOns.find((a) => a.id === addonId);
      return sum + (addon ? this.getAddOnBillablePriceCents(addon) : 0);
    }, 0);

    return this.centsToDollars(planCents + addOnsCents);
  }

  /**
   * Check if a plan is currently selected (either in-progress selection or already active)
   */
  public isCurrentPlan(planId: string): boolean {
    // Check if this is the plan being selected right now
    if (this.selectedPlanId === planId) {
      return true;
    }

    // Check if this is the active plan
    if (this.showCompanyBased && this.company) {
      const normalized = this.planService.normalizePlanId(this.company.planId);
      return normalized === planId;
    } else if (this.user) {
      return this.user.planId === planId;
    }
    return false;
  }

  /**
   * Get the display name for a plan button
   */
  public getPlanButtonText(planId: string): string {
    if (this.selectedPlanId === planId) {
      return 'Selected';
    }
    if (this.isCurrentPlan(planId) && !this.isRegistrationFlow) {
      return 'Current plan';
    }

    const plan = this.plans.find((p) => p.uid === planId);
    if (plan?.flags?.contactSales) return 'Contact us';
    if (plan?.appTier === 'free') {
      return this.isCurrentPlan(planId) ? 'Current plan' : 'Select plan';
    }
    return 'Select plan';
  }

  /**
   * Get the CSS class for a plan button
   */
  public getPlanButtonClass(planId: string): string {
    if (this.isCurrentPlan(planId)) {
      return 'btn btn-secondary';
    }

    const plan = this.plans.find((p) => p.uid === planId);
    if (plan?.isFeatured) {
      return 'btn btn-primary';
    }

    return 'btn btn-outline-primary';
  }

  /**
   * Check if a plan button should be disabled
   */
  public isPlanButtonDisabled(planId: string): boolean {
    return this.isCurrentPlan(planId);
  }

  /**
   * Get remaining trial days for a company
   */
  public getTrialDaysRemaining(company: Company): number {
    if (!company || company.tier !== 'trial') {
      return 0;
    }

    // Sales sets the end date when granting a trial, so it wins (#847).
    const trialEnd = this.tierAccessService.getTrialEndDate(company);
    const now = new Date();

    if (!trialEnd || now >= trialEnd) {
      return 0;
    }

    return Math.ceil(
      (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
  }

  /**
   * Sends the add-on changes for the current plan to Scan Ninja as a request
   * (#945). `company.addons` and `company.featureFlags` are server-owned, so
   * nothing is written to the company: staff confirm the price and turn the
   * add-ons on from the admin console. Coming-soon selections are sent as
   * interest.
   *
   * @returns The outcome, with a message to show.
   */
  private async requestCompanyAddOnChanges(): Promise<AddonChangeResult> {
    if (!this.company) {
      return { success: false, message: ADDON_REQUEST_FAILED_MESSAGE };
    }
    const { requested, removed } = AddonRequestService.diff(
      this.company.addons ?? [],
      this.selectedAddOns,
    );
    const interest = [...this.selectedAddOnIntents];
    if (requested.length + removed.length + interest.length === 0) {
      return { success: true, message: 'No add-on changes to send.' };
    }
    return firstValueFrom(this.addonRequestService.request({ requested, removed, interest }));
  }

  /**
   * Returns only the features that are new in the plan at the given index,
   * filtering out any features already present in lower-tier plans.
   * Uses caching to avoid recomputation on each change detection cycle.
   */
  public getUniqueFeatures(index: number): any[] {
    // Return cached result if available
    if (this.featuresCache.has(index)) {
      return this.featuresCache.get(index)!;
    }

    if (!this.plans[index]?.features) {
      return [];
    }

    // Build set of feature names from previous plans
    const prevFeatureNames = new Set<string>();
    for (let i = 0; i < index; i++) {
      const prevFeatures = this.getFeaturesArray(this.plans[i].features);
      prevFeatures.forEach((f) => {
        if (f.type === 'boolean' && f.value) {
          prevFeatureNames.add(f.name);
        }
      });
    }

    // Get current plan features, filter to unique ones
    const currentFeatures = this.getFeaturesArray(this.plans[index].features);
    const uniqueFeatures = currentFeatures.filter((f) => {
      // Always include number/string features (e.g., asset counts)
      if (f.type !== 'boolean') {
        return true;
      }
      // Boolean: include only if not seen in previous plans
      return !prevFeatureNames.has(f.name);
    });

    // Cache the result
    this.featuresCache.set(index, uniqueFeatures);
    return uniqueFeatures;
  }

  // ── Checkout bar (#956) ────────────────────────────────────────────────────

  /** Whether this user may buy for the company. */
  public get canPurchase(): boolean {
    return canPurchaseForCompany(this.user);
  }

  /** The step the customer is on. */
  public get planStep(): PlanStep {
    return this.checkoutSummary ? 3 : 1;
  }

  /** Steps done so far. */
  public get planStepsDone(): PlanStep[] {
    return this.checkoutSummary ? [1] : [];
  }

  /** Scrolls to a step when the step guidance is used. */
  public goToStep(step: PlanStep): void {
    if (typeof document === 'undefined') return;
    if (step === 3) {
      document.querySelector<HTMLElement>('[data-test="checkout-primary"]')?.focus();
      return;
    }
    const target = document.querySelector(step === 2 ? '.sn-token-usage-section' : '.cyber-plans-grid');
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** True when this plan is the one staged in the checkout bar. */
  public isStagedPlan(planId: string): boolean {
    return !!this.checkoutSummary && this.selectedPlanId === planId;
  }

  /** The bar's primary action: the existing confirm handler, nothing new. */
  public async onCheckoutPrimary(): Promise<void> {
    if (this.checkoutBusy || !this.checkoutSummary) return;
    this.checkoutBusy = true;
    try {
      await this.confirmPlanSelection();
    } finally {
      this.checkoutBusy = false;
      this.cdr.markForCheck();
    }
  }

  /** "Clear selection": back to what the company has. */
  public clearSelection(): void {
    const current = this.currentPlanId();
    this.selectedPlanId = current && this.plans.some((p) => p.uid === current) ? current : null;
    this.selectedAddOns = Array.isArray(this.company?.addons) ? [...(this.company!.addons as string[])] : [];
    this.selectedAddOnIntents = [];
    this.selectionAnnouncement = 'Selection cleared.';
    const scope = this.selectionScope;
    if (scope) clearCheckoutSelection(scope);
    this.refreshCheckout();
  }

  /** Rebuilds what the checkout bar shows. */
  public refreshCheckout(): void {
    this.checkoutSummary = this.buildCheckoutSummary();
  }

  private currentPlanId(): string | null {
    return this.company?.planId ? this.planService.normalizePlanId(this.company.planId) : null;
  }

  private sameAddOns(a: readonly string[], b: readonly string[]): boolean {
    return a.length === b.length && a.every((id) => b.includes(id));
  }

  private buildCheckoutSummary(): CheckoutBarSummary | null {
    if (!this.showCompanyBased || !this.company || this.isPartnerCompany || !this.canPurchase) return null;
    const plan = this.getSelectedPlan();
    if (!plan) return null;
    const current = this.currentPlanId();
    const companyAddOns = Array.isArray(this.company.addons) ? (this.company.addons as string[]) : [];
    const addOnsChanged = !this.sameAddOns(this.selectedAddOns, companyAddOns) || this.selectedAddOnIntents.length > 0;
    if (plan.uid === current && !addOnsChanged) return null;

    const tier = String(this.company.tier ?? 'free');
    const hasPaidPlan = !!current && !['free', 'trial', 'expired'].includes(tier);
    const addOnNames = [...this.selectedAddOns, ...this.selectedAddOnIntents]
      .map((id) => this.addOns.find((a) => a.id === id)?.name ?? id);
    const quote = !this.advertisePricing || !!plan.flags?.contactSales || !plan.hasPrice || !!plan.isFree;
    const base: CheckoutBarSummary = {
      mode: quote ? 'quote' : hasPaidPlan ? 'change' : 'new',
      title: `${plan.name} plan`,
      interval: quote ? null : this.billingInterval === 'monthly' ? 'Billed monthly' : 'Billed annually',
      addOns: addOnNames,
      priceNow: null,
      regularPrice: null,
      thenText: null,
      priceNote: null,
    };
    if (quote) return { ...base, priceNote: 'Quoted by our team' };
    if (plan.uid === current) return { ...base, mode: 'change', priceNote: 'Our team confirms the add-on price' };

    const addOnCents = this.selectedAddOns.reduce((sum, id) => {
      const addOn = this.addOns.find((a) => a.id === id);
      return sum + (addOn ? this.getAddOnBillablePriceCents(addOn) : 0);
    }, 0);
    const nowCents = this.getPlanPriceCents(plan) + addOnCents;
    const listCents = this.getPlanListPriceCents(plan) + addOnCents;
    const discounted = this.isPlanDiscounted(plan) && nowCents < listCents;
    const money = (cents: number) =>
      new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100);
    return {
      ...base,
      priceNow: money(nowCents),
      regularPrice: discounted ? money(listCents) : null,
      thenText: discounted && this.appliedPromo
        ? promoThenText(this.appliedPromo.durationMonths, `${money(listCents)}${this.getBillingLabel()}`)
        : null,
    };
  }

  private get selectionScope(): string | null {
    return this.company?.uid ? `plan-step.${this.company.uid}` : null;
  }

  private persistSelection(): void {
    const scope = this.selectionScope;
    if (!scope || typeof window === 'undefined') return;
    if (!this.checkoutSummary && !this.appliedPromo) {
      clearCheckoutSelection(scope);
      return;
    }
    saveCheckoutSelection(scope, {
      planId: this.checkoutSummary ? this.selectedPlanId : null,
      interval: this.billingInterval,
      addOnIds: [...this.selectedAddOns],
      packId: null,
      promoCode: this.appliedPromo?.code ?? null,
    });
  }

  /** Puts back the session's selection after the plans load. */
  private restoreSelection(): void {
    if (this.selectionRestored || typeof window === 'undefined') return;
    const scope = this.selectionScope;
    if (!scope || this.isPartnerCompany || !this.canPurchase) return;
    this.selectionRestored = true;
    const saved = readCheckoutSelection(scope);
    if (!saved) return;
    this.billingInterval = saved.interval;
    if (saved.planId && this.plans.some((p) => p.uid === saved.planId)) {
      this.selectedPlanId = saved.planId;
      this.selectedAddOns = [...saved.addOnIds];
      this.normalizeSelectedAddOnsForPlan();
    }
    if (saved.promoCode && this.promoCodesEnabled && this.advertisePricing && !this.promoCodeInput.trim()) {
      this.promoCodeInput = saved.promoCode;
      void this.applyPromoCode();
    }
  }
}
